import React, { useEffect, useState } from 'react';
import { CalendarSync as SyncIcon, Copy, Check, Upload, Loader2, RefreshCw, AlertTriangle, X, Link2, Unlink, DownloadCloud } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { calendar, uploads } from '../../lib/api';
import { toast } from 'sonner';

// Both halves of calendar sync in one panel, because they are the same question
// asked twice: which schedule is this, and which direction is it going?
//
// The import always runs as a dry run first. A schedule is acted on by people
// who will not re-check it, so the office sees the exact counts — and every
// event the file could not be read cleanly — before anything is written.
export default function CalendarSync({ onClose, onImported }) {
  const [feed, setFeed] = useState(null);
  const [source, setSource] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState('');
  const [plan, setPlan] = useState(null);
  const [file, setFile] = useState(null);
  const [attachmentId, setAttachmentId] = useState('');

  useEffect(() => { calendar.feedUrl().then(setFeed).catch(() => {}); }, []);
  useEffect(() => {
    calendar.getSource().then((s) => { setSource(s); setSourceUrl(s.url || ''); }).catch(() => {});
  }, []);

  // Connecting checks the address reads before it is stored, so a wrong paste
  // fails here rather than the first time someone presses Sync.
  const connect = async () => {
    setBusy('connect');
    try {
      const res = await calendar.setSource(sourceUrl, source?.autoSync || false);
      setSource(await calendar.getSource());
      toast.success(`Connected — ${res.eventsFound} appointment(s) on that calendar`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not connect that calendar');
    } finally { setBusy(''); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this calendar? Jobs already imported stay on the schedule.')) return;
    setBusy('disconnect');
    try {
      await calendar.clearSource();
      setSource(await calendar.getSource());
      setSourceUrl('');
      setPlan(null);
      toast.success('Calendar disconnected');
    } catch { toast.error('Could not disconnect'); } finally { setBusy(''); }
  };

  // Always a dry run first. The office sees what it would do before it does it,
  // exactly as with the file upload.
  const syncNow = async () => {
    setBusy('sync'); setPlan(null); setFile(null); setAttachmentId('');
    try {
      setPlan(await calendar.sync(true));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that calendar');
    } finally { setBusy(''); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the link and copy it by hand');
    }
  };

  const rotate = async () => {
    if (!window.confirm('Issue a new link? Every calendar already subscribed to the old one will stop updating.')) return;
    setBusy('rotate');
    try {
      await calendar.rotateFeed();
      setFeed(await calendar.feedUrl());
      toast.success('New link issued — subscribe again with it');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not issue a new link');
    } finally { setBusy(''); }
  };

  // The file is uploaded once and then planned by id, so pressing Apply does
  // not re-send it — and so the file that was applied is the one that was
  // reviewed, not a second read of a picker that may have changed.
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f); setPlan(null); setAttachmentId(''); setBusy('upload');
    try {
      const res = await uploads.upload(f, { kind: 'calendar', linkedType: 'none', note: 'calendar import' });
      const id = res?.files?.[0]?.id;
      setAttachmentId(id);
      setBusy('plan');
      setPlan(await calendar.importIcs({ attachmentId: id, dryRun: true }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that calendar file');
      setFile(null);
    } finally { setBusy(''); }
  };

  const apply = async () => {
    setBusy('apply');
    try {
      const res = attachmentId
        ? await calendar.importIcs({ attachmentId, dryRun: false })
        : await calendar.sync(false);
      toast.success(`${res.created} job(s) added, ${res.updated} updated`);
      setPlan(res);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed — nothing was changed');
    } finally { setBusy(''); }
  };

  const n = (k) => (plan ? plan[k] ?? 0 : 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="calendar-sync-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><SyncIcon className="w-5 h-5 text-[#8BC53F]" />Calendar sync</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          <section>
            <h4 className="font-bold text-slate-900">Show this schedule in a calendar app</h4>
            <p className="text-sm text-slate-600 mt-1">
              Add this link in Google Calendar (Other calendars → From URL), Apple Calendar or Outlook.
              It updates on its own and is read-only.
            </p>
            {feed ? (
              <>
                <div className="mt-3 flex gap-2">
                  <input readOnly value={feed.url} onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 text-xs font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                    data-testid="calendar-feed-url" />
                  <Button variant="outline" onClick={copy} className="gap-1.5 flex-shrink-0">
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Anyone with this link can read the schedule without signing in — customer names and addresses included.
                </p>
                <Button variant="outline" size="sm" onClick={rotate} disabled={busy === 'rotate'} className="mt-2 gap-1.5" data-testid="calendar-rotate-btn">
                  {busy === 'rotate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Issue a new link
                </Button>
              </>
            ) : <div className="mt-3 text-sm text-slate-400">Loading…</div>}
          </section>

          <section className="border-t border-slate-200 pt-5">
            <h4 className="font-bold text-slate-900">Bring jobs in from Google Calendar</h4>
            <p className="text-sm text-slate-600 mt-1">
              Connect the calendar once, then sync with a button. In Google Calendar open
              <span className="font-semibold"> Settings → the calendar → “Secret address in iCal format”</span> and
              paste it here. It is read-only, and resetting it there disconnects this.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
                className="flex-1 min-w-0 text-xs font-mono bg-white border border-slate-200 rounded-md px-3 py-2"
                data-testid="calendar-source-url" />
              <Button variant="outline" onClick={connect} disabled={!sourceUrl.trim() || busy === 'connect'}
                className="gap-1.5 flex-shrink-0" data-testid="calendar-connect-btn">
                {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {source?.url ? 'Update' : 'Connect'}
              </Button>
            </div>

            {source?.url && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button onClick={syncNow} disabled={busy === 'sync'}
                  className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="calendar-sync-now-btn">
                  {busy === 'sync' ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
                  Sync appointments now
                </Button>
                <Button variant="outline" size="sm" onClick={disconnect} disabled={busy === 'disconnect'} className="gap-1.5">
                  <Unlink className="w-3.5 h-3.5" />Disconnect
                </Button>
                <span className="text-xs text-slate-500">
                  {source.lastSyncedAt
                    ? `Last synced ${new Date(source.lastSyncedAt).toLocaleString()}`
                    : 'Not synced yet'}
                </span>
              </div>
            )}
          </section>

          <section className="border-t border-slate-200 pt-5">
            <h4 className="font-bold text-slate-900">Or import a calendar file</h4>
            <p className="text-sm text-slate-600 mt-1">
              A one-off export, or a calendar that has no shareable address. Nothing is written until you have seen what it would do.
            </p>
            <label className="mt-3 inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
              {busy === 'upload' || busy === 'plan' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {file ? file.name : 'Choose an .ics file'}
              <input type="file" accept=".ics,text/calendar" className="hidden" onChange={onFile} data-testid="calendar-ics-input" />
            </label>

            {plan && (
              <Card className="mt-4 p-4 border-slate-200 bg-slate-50" data-testid="calendar-import-plan">
                <div className="text-sm font-bold text-slate-900">
                  {plan.eventsRead} event(s) read · {plan.window?.from} to {plan.window?.to}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 text-sm">
                  <Stat label="New jobs" value={plan.dryRun ? n('insert') : n('created')} tone="emerald" />
                  <Stat label="Rescheduled" value={plan.dryRun ? n('update') : n('updated')} tone="amber" />
                  <Stat label="Already matching" value={n('unchanged')} />
                  <Stat label="No customer matched" value={n('withoutACustomer')} tone={n('withoutACustomer') ? 'amber' : ''} />
                  <Stat label="Cancelled on calendar" value={n('cancelledOnCalendar')} />
                  <Stat label="Skipped" value={n('skipped')} tone={n('skipped') ? 'amber' : ''} />
                </div>

                {n('cancelledOnCalendar') > 0 && (
                  <p className="text-xs text-slate-600 mt-3">
                    Jobs cancelled on the calendar are listed, never deleted here — cancel them on the schedule if that is right.
                  </p>
                )}

                {plan.warnings?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-bold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Needs a look</div>
                    <ul className="mt-1 space-y-1 text-xs text-amber-800 list-disc pl-5">
                      {plan.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
                      {plan.warnings.length > 8 && <li>…and {plan.warnings.length - 8} more</li>}
                    </ul>
                  </div>
                )}

                {plan.sampleInserts?.length > 0 && plan.dryRun && (
                  <div className="mt-3">
                    <div className="text-xs font-bold text-slate-700">First few new jobs</div>
                    <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                      {plan.sampleInserts.slice(0, 6).map((r, i) => (
                        <li key={i}>{r.date} {r.time} · {r.service}{r.customer ? ` · ${r.customer}` : ' · (no customer matched)'}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {plan.dryRun ? (
                  <Button onClick={apply} disabled={busy === 'apply' || (n('insert') + n('update') === 0)}
                    className="mt-4 bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="calendar-apply-btn">
                    {busy === 'apply' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Apply to the schedule
                  </Button>
                ) : (
                  <div className="mt-4 text-sm font-bold text-emerald-700 flex items-center gap-1.5"><Check className="w-4 h-4" />Applied to the schedule</div>
                )}
              </Card>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-700';
  return (
    <div>
      <div className={`text-2xl font-extrabold tabular-nums ${color}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
